import { useState } from 'react';
import { Building2, RefreshCw, ShieldCheck, UserPlus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { adminOpsKeys, useFoundingPartnerRequests } from '../hooks/useAdminOps';
import EarlyAccessTab from './websiteLeads/EarlyAccessTab';
import FoundingPartnersTab from './websiteLeads/FoundingPartnersTab';

/**
 * DM-7703: website leads -- the Founding Partner follow-up pipeline and the
 * Early Access waitlist the website forms persist since the Formspree
 * migration, including the imported Formspree history.
 */

const TABS = [
  { id: 'founding-partners', label: 'Founding Partners' },
  { id: 'early-access', label: 'Early Access' },
] as const;
type TabId = (typeof TABS)[number]['id'];

export default function WebsiteLeads() {
  const [tab, setTab] = useState<TabId>('founding-partners');
  const queryClient = useQueryClient();
  const refresh = () =>
    Promise.all(
      (['founding-partner-requests', 'early-access-leads'] as const).map(name =>
        queryClient.invalidateQueries({ queryKey: adminOpsKeys.queue(name) })
      )
    );
  const foundingPartners = useFoundingPartnerRequests({ status: 'new', source: 'all' });
  const newPartners = foundingPartners.summary?.by_status.new;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <UserPlus className="w-7 h-7 text-primary" />
            Website leads
          </h1>
          <p className="text-gray-text">
            Founding Partner requests and the Early Access waitlist from donate-mate.com, including
            the history imported from Formspree.
          </p>
          <p className="text-xs text-gray-text mt-2 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" />
            Contact details are personal data. Each view is recorded in the audit log as website
            lead follow-up.
          </p>
        </div>
      </div>

      <div className="glass-card p-2 flex items-center gap-1 flex-wrap" role="tablist">
        {TABS.map(entry => (
          <button
            key={entry.id}
            role="tab"
            aria-selected={tab === entry.id}
            onClick={() => setTab(entry.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              tab === entry.id ? 'bg-primary/20 text-primary' : 'text-gray-text hover:text-white'
            }`}
          >
            {entry.id === 'founding-partners' ? (
              <Building2 className="w-4 h-4" />
            ) : (
              <UserPlus className="w-4 h-4" />
            )}
            {entry.label}
            {entry.id === 'founding-partners' && newPartners !== undefined && newPartners > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-primary/30 text-primary text-xs">
                {newPartners} new
              </span>
            )}
          </button>
        ))}
        <button
          onClick={refresh}
          className="ml-auto p-2 text-gray-text hover:text-white"
          aria-label="Refresh"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${foundingPartners.isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {tab === 'founding-partners' ? <FoundingPartnersTab /> : <EarlyAccessTab />}
    </div>
  );
}
